import type { Table } from 'dexie';
import { syncCollections, type HouseholdChanges } from '../domain/types';
import type { NestDatabase } from './local-db';
import { applySnapshot } from './snapshot';

export async function applyChanges(db: NestDatabase, changes: HouseholdChanges): Promise<boolean> {
  if (changes.kind === 'snapshot') return applySnapshot(db, changes.snapshot);
  const id = changes.household.id;
  const tables = { memberships: db.memberships, categories: db.categories, paymentMethods: db.paymentMethods, transactions: db.transactions, budgets: db.budgets, recurringTemplates: db.recurringTemplates, recurringOccurrences: db.occurrences };
  return db.transaction('rw', [db.meta, db.households, db.outbox, db.notifications, ...Object.values(tables)], async () => {
    const revision = String((await db.meta.get(`revision:${id}`))?.value ?? '0');
    // A delta must begin at exactly the local cursor; never skip unseen changes.
    if (revision !== changes.fromRevision || BigInt(changes.revision) < BigInt(revision)) return false;
    const minimum = String((await db.meta.get(`minimumRevision:${id}`))?.value ?? '0');
    if (BigInt(changes.revision) < BigInt(minimum)) return false;
    for (const key of syncCollections) {
      if (changes[key].some(row => row.householdId !== id)) throw new Error('Invalid household change.');
      const table = tables[key] as Table;
      const deletedKeys = key === 'memberships' ? changes.removed[key].map(profileId => [id, profileId]) : changes.removed[key];
      // Limit deletions to this household even if a malformed response contains another ID.
      if (key === 'memberships') await table.bulkDelete(deletedKeys);
      else if (deletedKeys.length) await table.where('householdId').equals(id).and(row => deletedKeys.includes(row.id)).delete();
      if (changes[key].length) await table.bulkPut(changes[key]);
    }
    if (changes.notifications.some(row => row.householdId !== id)) throw new Error('Invalid household notification.');
    await db.households.put(changes.household);
    await db.notifications.where('householdId').equals(id).delete();
    if (changes.notifications.length) await db.notifications.bulkPut(changes.notifications);
    await db.meta.put({ key: `revision:${id}`, value: changes.revision });
    await db.meta.put({ key: `lastSync:${id}`, value: new Date().toISOString() });
    await db.outbox.where('[householdId+state]').equals([id, 'acknowledged'])
      .filter(op => op.acknowledgedRevision !== undefined && BigInt(op.acknowledgedRevision) <= BigInt(changes.revision)).delete();
    return true;
  });
}
